import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { toast } from 'sonner'
import { apiGet, apiPatch, apiPost } from '@/api/axios-client'
import { getErrorMessage } from '@/api/errors'
import { qk } from '@/api/query-keys'
import type { Project } from '@/types/entities'
import type { Dashboard, LifecycleSummary, MigrationResult, RegistryTask, RelatedProject } from './model'

/** A GoHighLevel deal with no Client Project yet, pre-filled for its AM to confirm. */
export interface CrmDraft {
  dealId: string
  name: string
  stage: string
  status: string
  value: number
  enteredStageAt: string | null
  createdAt: string | null
  owner: { ghlUserId: string | null; name: string | null; email: string | null; memberId: string | null }
  company: { key: string | null; name: string; domain: string | null; accountKey: string | null }
  contacts: Array<{ name: string; email: string | null; phone: string | null; primary: boolean }>
  products: string[]
  description: string | null
  pilotStartAt: string | null
  pilotEndAt: string | null
  suggestions: Array<{ id: string; name: string; why: 'remembered' | 'same name' | 'similar name' }>
}

export interface CrmDeal {
  dealId: string
  role: 'sales' | 'post_sales'
  found: boolean
  name: string | null
  pipeline: string | null
  stage: string | null
  status: string | null
  value: number | null
  owner: string | null
  accountKey: string | null
  enteredStageAt: string | null
  inCharge: string
}

const crmDraftsKey = ['crm-projects', 'drafts'] as const

export function useCrmDrafts(enabled: boolean) {
  return useQuery({
    queryKey: crmDraftsKey,
    queryFn: () => apiGet<{ connected: boolean; items: CrmDraft[] }>('crm-projects/drafts'),
    enabled,
    staleTime: 60_000,
    retry: false,
  })
}

export function useDismissCrmDraft() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (dealId: string) => apiPost<{ ok: true }>(`crm-projects/drafts/${encodeURIComponent(dealId)}/dismiss`),
    retry: false,
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: crmDraftsKey })
      toast.success('Dismissed: it will not be suggested again')
    },
    onError: (error) => toast.error(getErrorMessage(error)),
  })
}

/** Link a draft's deal to a Client Project already open, instead of opening another. */
export function useMergeCrmDraft() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: ({ dealId, projectId }: { dealId: string; projectId: string }) =>
      apiPost<{ ok: true; projectId: string; deal: string }>(`crm-projects/drafts/${encodeURIComponent(dealId)}/merge`, { projectId }),
    retry: false,
    onSuccess: (r) => {
      void qc.invalidateQueries({ queryKey: ['crm-projects'] })
      toast.success(`Merged: ${r.deal} is now linked to that Client Project`)
    },
    onError: (error) => toast.error(getErrorMessage(error)),
  })
}

/** Take a deal off a Client Project. An open Sales deal shows as a draft again. */
export function useUnlinkProjectDeal(projectId: string) {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (dealId: string) => apiPost<{ ok: true }>(`crm-projects/projects/${encodeURIComponent(projectId)}/deals/${encodeURIComponent(dealId)}/unlink`),
    retry: false,
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['crm-projects'] })
      toast.success('Unlinked from this project')
    },
    onError: (error) => toast.error(getErrorMessage(error)),
  })
}

export const refreshCrmDrafts = (qc: ReturnType<typeof useQueryClient>) => void qc.invalidateQueries({ queryKey: crmDraftsKey })

export function useProjectDeals(projectId: string) {
  return useQuery({
    queryKey: ['crm-projects', 'deals', projectId],
    queryFn: () => apiGet<{ connected: boolean; items: CrmDeal[] }>(`crm-projects/projects/${encodeURIComponent(projectId)}/deals`),
    enabled: Boolean(projectId),
    staleTime: 60_000,
    retry: false,
  })
}

const base = (projectId: string) => `project-lifecycles/${encodeURIComponent(projectId)}`

export function useLifecycles(enabled = true) {
  return useQuery({
    queryKey: qk.lifecycles(),
    queryFn: () => apiGet<{ items: LifecycleSummary[] }>('project-lifecycles').then((r) => r.items),
    enabled,
    staleTime: 60_000,
  })
}

/**
 * The Client Projects, for the sidebar: one page per client under Projects. Reads the same
 * project list the Projects page does (same key, same shape), and narrows it.
 */
export function useClientProjectsNav(enabled: boolean) {
  return useQuery({
    queryKey: qk.projects(),
    queryFn: () => apiGet<{ items: Project[] }>('projects'),
    enabled,
    staleTime: 60_000,
    select: (r) =>
      r.items
        .filter((p) => p.type === 'client' && !p.archived)
        .map((p) => ({ id: p.id, label: p.name.replace(/\s+[–-]\s+Client Project$/i, '') }))
        .sort((a, b) => a.label.localeCompare(b.label)),
  })
}

/**
 * Every live project for the Project Management sidebar, split by type: Client Projects (one per
 * client and region, opening their dashboard) and internal projects (opening the project page).
 * Same key as the Projects page, so it costs no extra request.
 */
export function useProjectsNav(enabled: boolean) {
  return useQuery({
    queryKey: qk.projects(),
    queryFn: () => apiGet<{ items: Project[] }>('projects'),
    enabled,
    staleTime: 60_000,
    select: (r) => {
      const live = r.items.filter((p) => !p.archived)
      const byLabel = (a: { label: string }, b: { label: string }) => a.label.localeCompare(b.label)
      return {
        client: live.filter((p) => p.type === 'client').map((p) => ({ id: p.id, label: p.name.replace(/\s+[–-]\s+Client Project$/i, '') })).sort(byLabel),
        internal: live.filter((p) => p.type === 'internal').map((p) => ({ id: p.id, label: p.name })).sort(byLabel),
      }
    },
  })
}

export function useRelatedProjects(projectId: string) {
  return useQuery({ queryKey: qk.lifecycleRelated(projectId), queryFn: () => lifecycleApi.related(projectId).then((r) => r.items), enabled: Boolean(projectId), staleTime: 60_000 })
}

export function useLifecycle(projectId: string) {
  return useQuery({
    queryKey: qk.lifecycle(projectId),
    queryFn: () => apiGet<Dashboard>(base(projectId)),
    enabled: Boolean(projectId),
  })
}

export const lifecycleApi = {
  create: (body: unknown) => apiPost<Dashboard>('project-lifecycles', body),
  patch: (id: string, body: unknown) => apiPatch<Dashboard>(base(id), body),
  advance: (id: string, body: { checks: string[]; decision: 'go' | 'no_go' | null; note: string; carryOpenTasks: boolean }) => apiPost<Dashboard>(`${base(id)}/advance`, body),
  openCycle: (id: string, body: { name: string; scope: string; skipPilot: boolean; products: string[] }) => apiPost<Dashboard>(`${base(id)}/cycles`, body),
  extendPilot: (id: string, body: { days: number; reason: string }) => apiPost<Dashboard>(`${base(id)}/pilot-extension`, body),
  offboard: (id: string, body: { note: string }) => apiPost<Dashboard>(`${base(id)}/offboard`, body),
  addTask: (id: string, body: unknown) => apiPost<RegistryTask>(`${base(id)}/tasks`, body),
  patchTask: (id: string, taskId: string, body: unknown) => apiPatch<RegistryTask>(`${base(id)}/tasks/${encodeURIComponent(taskId)}`, body),
  archiveTask: (id: string, taskId: string) => apiPost<RegistryTask>(`${base(id)}/tasks/${encodeURIComponent(taskId)}/archive`),
  flagTasks: (id: string, keys?: string[]) => apiPost<Dashboard>(`${base(id)}/flags/tasks`, keys ? { keys } : {}),
  clearFlag: (id: string, key: string) => apiPost<Dashboard>(`${base(id)}/flags/clear`, { key }),
  escalate: (id: string, note: string) => apiPost<Dashboard>(`${base(id)}/escalations`, { note }),
  related: (id: string) => apiGet<{ items: RelatedProject[] }>(`${base(id)}/related`),
  migrate: (body: { dryRun: boolean; sameAs?: Record<string, string>; selected?: string[]; names?: Record<string, string>; main?: Record<string, string>; order?: Record<string, number> }) => apiPost<MigrationResult>('project-lifecycles/migrate', body),
}

/**
 * A write on one Client Project. Every route that changes the lifecycle answers with the whole
 * dashboard, so the cache is replaced rather than refetched; task writes answer with the task,
 * so those refetch. No retry: a retried phase move or task create is a second one.
 */
export function useLifecycleWrite<V>(projectId: string, fn: (vars: V) => Promise<Dashboard | RegistryTask>, done?: string) {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: fn,
    retry: false,
    onSuccess: (data) => {
      if (data && typeof data === 'object' && 'lifecycle' in data) qc.setQueryData(qk.lifecycle(projectId), data)
      else void qc.invalidateQueries({ queryKey: qk.lifecycle(projectId) })
      void qc.invalidateQueries({ queryKey: qk.lifecycles() })
      void qc.invalidateQueries({ queryKey: qk.project(projectId) })
      if (done) toast.success(done)
    },
    onError: (error) => toast.error(getErrorMessage(error)),
  })
}
